<template>
  <WeaponPicker
    v-model="pickerOpen"
    :heading="pickerHeading"
    :current-slugs="pickerContext?.currentSlugs ?? EMPTY_SLUGS"
    :forbidden-slugs="pickerContext?.forbiddenSlugs ?? EMPTY_SLUGS"
    :weapon-slugs="tankWeaponFilter"
    @pick="onPickerPick"
  />
</template>

<script setup lang="ts">
/**
 * PickerController — wraps the global `WeaponPicker` modal and exposes a
 * small, typed API for the planner's two-step / single-step weapon-selection
 * flows. The host (`GvgPlannerView`) holds a `ref` to this component so any
 * nested card can request a picker without managing the modal lifecycle
 * itself.
 *
 * Four picker kinds are exposed:
 *
 *  - `'alt'` — replacing one slot of a player's alt-weapon pair (forbidden
 *    = main + alt).
 *  - `'build'` — two-step personal build creator.
 *  - `'guild-build'` — two-step GUILD library creator (no player scope; the
 *    cap is per-guild).
 *  - `'guild-build-slot'` — single-step swap of one slot of an existing
 *    guild build (forbidden = partner slot).
 */
import { computed, ref } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { EMPTY_SLUGS } from '../../lib/empty'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import { weaponsEn } from './locales/en.ts'
import { weaponsRu } from './locales/ru.ts'
import WeaponPicker from './WeaponPicker.vue'

import type { GuildBuildEntry } from '../../types'
import { PlayerRole } from '@/types/weapon'

export interface PickerControllerProps {
  readonly disabled?: boolean
}

type PickerKind =
  | 'alt'
  | 'build'
  | 'guild-build'
  | 'guild-build-slot'

interface PickerContext {
  readonly kind: PickerKind
  readonly playerId?: string
  readonly buildId?: string
  readonly slot?: 0 | 1
  readonly currentSlugs: ReadonlyArray<string>
  readonly forbiddenSlugs: ReadonlyArray<string>
}

withDefaults(defineProps<PickerControllerProps>(), {
  disabled: false,
})

const catalog = defineLocaleCatalog({ en: weaponsEn, ru: weaponsRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()
const pickerOpen = ref(false)
const pickerContext = ref<PickerContext | null>(null)

const pickerHeading = computed<string>(() => {
  const context = pickerContext.value
  if (context === null) return messages.value.picker.chooseWeapon
  if (context.kind === 'build') {
    return context.currentSlugs.length === 0
      ? messages.value.controller.chooseFirstBuildWeapon
      : messages.value.controller.chooseSecondBuildWeapon
  }
  if (context.kind === 'guild-build') {
    return context.currentSlugs.length === 0
      ? messages.value.controller.chooseFirstGuildBuildWeapon
      : messages.value.controller.chooseSecondGuildBuildWeapon
  }
  if (context.kind === 'guild-build-slot') {
    return context.slot === 0
      ? messages.value.controller.chooseFirstGuildBuildWeapon
      : messages.value.controller.chooseSecondGuildBuildWeapon
  }
  return messages.value.controller.addAltWeapon
})

const tankWeaponFilter = computed<ReadonlyArray<string> | null>(() => {
  const context = pickerContext.value
  if (context === null) return null
  if (context.playerId === undefined) return null
  const player = store.playerById(context.playerId)
  return player?.role === PlayerRole.Tank ? ['thundercry_blade'] : null
})

/**
 * Walk every player's `guildBuilds` for the first match. The library is
 * the same shape on every player row, so a single scan resolves any
 * `buildId` we have in hand.
 */
function findGuildBuildEntry(buildId: string): GuildBuildEntry | null {
  for (const p of store.currentPlayers) {
    for (const entry of p.guildBuilds) {
      if (entry.id === buildId) return entry
    }
  }
  return null
}

function openAltPicker(playerId: string): void {
  const player = store.playerById(playerId)
  if (player === undefined) return
  pickerContext.value = {
    kind: 'alt',
    playerId,
    currentSlugs: player.altWeapons,
    forbiddenSlugs: [...player.mainWeapons, ...player.altWeapons],
  }
  pickerOpen.value = true
}

function openNewBuildPicker(playerId: string): void {
  const player = store.playerById(playerId)
  if (player === undefined) return
  pickerContext.value = {
    kind: 'build',
    playerId,
    currentSlugs: EMPTY_SLUGS,
    forbiddenSlugs: EMPTY_SLUGS,
  }
  pickerOpen.value = true
}

function openNewGuildBuildPicker(): void {
  // Guild-scoped: no playerId. The library is per-guild, not per-player.
  pickerContext.value = {
    kind: 'guild-build',
    currentSlugs: EMPTY_SLUGS,
    forbiddenSlugs: EMPTY_SLUGS,
  }
  pickerOpen.value = true
}

function openGuildBuildSlotPicker(playerId: string, buildId: string, slot: 0 | 1): void {
  const entry = findGuildBuildEntry(buildId)
  if (entry === null) return
  const weapons = entry.weaponSlugs
  if (weapons.length !== 2) return
  const otherSlot = slot === 0 ? 1 : 0
  const forbiddenSlug = weapons[otherSlot]
  if (forbiddenSlug === undefined) return
  pickerContext.value = {
    kind: 'guild-build-slot',
    playerId,
    buildId,
    slot,
    currentSlugs: weapons,
    forbiddenSlugs: [forbiddenSlug],
  }
  pickerOpen.value = true
}

function onPickerPick(slug: string): void {
  const context = pickerContext.value
  if (context === null) return
  if (context.forbiddenSlugs.includes(slug)) {
    closePicker()
    return
  }

  if (context.kind === 'alt') {
    if (context.playerId === undefined) {
      closePicker()
      return
    }
    store.addAltPair(context.playerId, slug)
    pickerContext.value = null
    return
  }

  if (context.kind === 'guild-build-slot') {
    const slot = context.slot
    const buildId = context.buildId
    const playerId = context.playerId
    if (slot === undefined || buildId === undefined || playerId === undefined) {
      closePicker()
      return
    }
    const existing = findGuildBuildEntry(buildId)
    if (existing === null || existing.weaponSlugs.length !== 2) {
      closePicker()
      return
    }
    const partnerSlot = slot === 0 ? 1 : 0
    if (existing.weaponSlugs[partnerSlot] === slug) {
      closePicker()
      return
    }
    const next = existing.weaponSlugs.map((currentSlug, index) => index === slot ? slug : currentSlug)
    void store.setGuildBuild(buildId, next)
    pickerContext.value = null
    return
  }

  if (context.currentSlugs.length === 0) {
    pickerContext.value = {
      ...context,
      currentSlugs: [slug],
      forbiddenSlugs: [slug],
    }
    // WeaponPicker closes after each pick. Re-open on the next microtask so
    // the second step keeps the first weapon visible and distinctness locked.
    queueMicrotask(() => {
      pickerOpen.value = true
    })
    return
  }

  const firstSlug = context.currentSlugs[0]
  if (firstSlug === undefined || firstSlug === slug) {
    closePicker()
    return
  }

  if (context.kind === 'guild-build') {
    store.addGuildBuild([firstSlug, slug])
  } else {
    if (context.playerId === undefined) {
      closePicker()
      return
    }
    store.addBuild(context.playerId, [firstSlug, slug])
  }
  pickerContext.value = null
}


function closePicker(): void {
  pickerOpen.value = false
  pickerContext.value = null
}

defineExpose({
  openAltPicker,
  openNewBuildPicker,
  openNewGuildBuildPicker,
  openGuildBuildSlotPicker,
  closePicker,
  isOpen: pickerOpen,
})
</script>
